import React, { useState, useCallback, useMemo, useRef } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useTranslation } from 'react-i18next'
import { type LifeSpace, type CreateLifeSpaceData } from '../../../hooks/useLifeSpaces'
import { getLifeSpaceIcon, AVAILABLE_ICON_NAMES } from '../../../lib/icons/iconRegistry'
import './CreateLifeSpaceDialog.scss'

const defaultColors = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFA07A', // Light Coral
  '#DDA0DD', // Plum
  '#F4A460', // Sandy Brown
  '#98D8C8', // Mint
  '#FFD93D', // Yellow
  '#6C63FF', // Purple
]

export interface CreateLifeSpaceDialogProps {
  /** Whether the dialog is shown. The form resets every time it opens. */
  open: boolean
  /** Reports `false` on cancel (Escape, backdrop click, Cancel button). */
  onOpenChange: (open: boolean) => void
  editingSpace?: LifeSpace | null
  onConfirm: (data: CreateLifeSpaceData) => void
}

/** The form itself — mounted only while the dialog is open, so it starts fresh. */
const CreateLifeSpaceForm: React.FC<Omit<CreateLifeSpaceDialogProps, 'open'>> = ({
  editingSpace,
  onConfirm,
  onOpenChange
}) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState(editingSpace?.name || '')
  const [selectedIcon, setSelectedIcon] = useState(editingSpace?.iconName || 'Star')
  const [selectedColor, setSelectedColor] = useState(editingSpace?.color || defaultColors[0])
  const [errors, setErrors] = useState<{ name?: string }>({})

  const validate = useCallback((): boolean => {
    const newErrors: { name?: string } = {}

    if (!name.trim()) {
      newErrors.name = t('lifeSpaces.nameRequired')
    } else if (name.length > 20) {
      newErrors.name = t('lifeSpaces.nameMaxLength')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [name, t])

  const handleConfirm = useCallback(() => {
    if (!validate()) return

    onConfirm({
      name: name.trim(),
      iconName: selectedIcon,
      color: selectedColor
    })
  }, [validate, name, selectedIcon, selectedColor, onConfirm])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && name.trim()) {
      handleConfirm()
    }
  }, [name, handleConfirm])

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
  }, [])

  const handleIconSelect = useCallback((iconName: string) => {
    setSelectedIcon(iconName)
  }, [])

  const handleColorSelect = useCallback((color: string) => {
    setSelectedColor(color)
  }, [])

  const dialogTitle = editingSpace ? t('lifeSpaces.editLifeSpace') : t('lifeSpaces.newLifeSpace')
  const buttonText = editingSpace ? t('lifeSpaces.save') : t('lifeSpaces.create')
  const inputClassName = `create-life-space-dialog__input ${errors.name ? 'create-life-space-dialog__input--error' : ''}`

  // Memoize icon grid items
  const iconGridItems = useMemo(() => {
    return AVAILABLE_ICON_NAMES.map((iconName) => {
      const Icon = getLifeSpaceIcon(iconName)
      const isSelected = selectedIcon === iconName

      return (
        <button
          key={iconName}
          className={`create-life-space-dialog__icon-button ${isSelected ? 'create-life-space-dialog__icon-button--selected' : ''}`}
          onClick={() => handleIconSelect(iconName)}
          style={{ color: selectedColor }}
        >
          <Icon size={20} />
        </button>
      )
    })
  }, [selectedIcon, selectedColor, handleIconSelect])

  // Memoize color grid items
  const colorGridItems = useMemo(() => {
    return defaultColors.map((color) => {
      const isSelected = selectedColor === color

      return (
        <button
          key={color}
          className={`create-life-space-dialog__color-button ${isSelected ? 'create-life-space-dialog__color-button--selected' : ''}`}
          onClick={() => handleColorSelect(color)}
          style={{ backgroundColor: color }}
        />
      )
    })
  }, [selectedColor, handleColorSelect])

  const PreviewIcon = getLifeSpaceIcon(selectedIcon)

  return (
    <>
      <Dialog.Overlay className="create-life-space-dialog__backdrop" />
      <Dialog.Content
        className="create-life-space-dialog"
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
        onKeyDown={handleKeyDown}
      >
        <div className="create-life-space-dialog__header">
          <Dialog.Title className="create-life-space-dialog__title">
            {dialogTitle}
          </Dialog.Title>
        </div>

        <div className="create-life-space-dialog__content">
          {/* Name Input */}
          <div className="create-life-space-dialog__field">
            <label>{t('lifeSpaces.nameLabel')}</label>
            <input
              ref={inputRef}
              type="text"
              className={inputClassName}
              value={name}
              onChange={handleNameChange}
              placeholder={t('lifeSpaces.namePlaceholder')}
              maxLength={20}
            />
            {errors.name && (
              <span className="create-life-space-dialog__error">{errors.name}</span>
            )}
            <span className="create-life-space-dialog__char-count">
              {name.length}/20
            </span>
          </div>

          {/* Icon Selection */}
          <div className="create-life-space-dialog__field">
            <label>{t('lifeSpaces.iconLabel')}</label>
            <div className="create-life-space-dialog__icon-grid">
              {iconGridItems}
            </div>
          </div>

          {/* Color Selection */}
          <div className="create-life-space-dialog__field">
            <label>{t('lifeSpaces.colorLabel')}</label>
            <div className="create-life-space-dialog__color-grid">
              {colorGridItems}
            </div>
          </div>

          {/* Preview */}
          <div className="create-life-space-dialog__preview">
            <label>{t('lifeSpaces.previewLabel')}</label>
            <div className="create-life-space-dialog__preview-space">
              <div
                className="life-space-button-preview"
                style={{ backgroundColor: `${selectedColor}20` }}
              >
                <PreviewIcon size={24} color={selectedColor} />
              </div>
              <span>{name || t('lifeSpaces.previewDefault')}</span>
            </div>
          </div>
        </div>

        <div className="create-life-space-dialog__actions">
          <button
            className="create-life-space-dialog__button create-life-space-dialog__button--secondary"
            onClick={() => onOpenChange(false)}
          >
            {t('lifeSpaces.cancel')}
          </button>
          <button
            className="create-life-space-dialog__button create-life-space-dialog__button--primary"
            onClick={handleConfirm}
            disabled={!name.trim()}
          >
            {buttonText}
          </button>
        </div>
      </Dialog.Content>
    </>
  )
}

const CreateLifeSpaceDialogComponent: React.FC<CreateLifeSpaceDialogProps> = ({ open, ...formProps }) => (
  <Dialog.Root open={open} onOpenChange={formProps.onOpenChange}>
    <Dialog.Portal>
      <CreateLifeSpaceForm {...formProps} />
    </Dialog.Portal>
  </Dialog.Root>
)

/**
 * Modal dialog for creating or editing a custom Life Space.
 *
 * @description
 * Renders a modal overlay with fields for name (max 20 chars), icon selection
 * (26 Lucide icons from the registry), color selection (10 preset colors),
 * and a live preview. Supports both create and edit mode based on the
 * `editingSpace` prop.
 *
 * Shell is Radix Dialog with the dialog's own look: portal, focus trap,
 * `Escape` and backdrop click report `onOpenChange(false)`; `Enter` confirms.
 * Focus starts in the name field.
 *
 * @example
 * // Create mode
 * <CreateLifeSpaceDialog
 *   open={showDialog}
 *   onOpenChange={setShowDialog}
 *   onConfirm={(data) => addSpace(data)}
 * />
 *
 * @example
 * // Edit mode
 * <CreateLifeSpaceDialog
 *   open={showDialog}
 *   onOpenChange={setShowDialog}
 *   editingSpace={selectedSpace}
 *   onConfirm={(data) => updateSpace(selectedSpace.id, data)}
 * />
 */
export const CreateLifeSpaceDialog = React.memo(CreateLifeSpaceDialogComponent)
